import { Amplify as AmplifyCore } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import {
  autoSignIn,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  deleteUser,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { captureException, logger, startSpan } from '../monitoring/sentry';
import { buildCloudflareUrl } from '../config/cloudflare';
import { isTransientNetworkError, withRetry } from '../utils/network-retry';
let refreshPromise = null;
let forceLogoutPromise = null;

const AUTH_FAILURE_MESSAGE_PATTERN =
  /not authorized|unauthorized|novalidauthtokens|no federated jwt|no current user|user unauthenticated|refresh token|token has expired|invalid.*token|session.*expired/i;

const AUTH_FAILURE_CODE_PATTERN =
  /notauthorized|unauthenticated|unauthorized|novalidauthtokens|nocurrentuser|tokenrefresh|invalidsession|sessionexpired/i;

const encodeStorageKey = (key) =>
  key
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');

const PUBLIC_STORAGE_PREFIX = 'public/';

const getPublicStorageKey = (key) =>
  key.startsWith(PUBLIC_STORAGE_PREFIX) ? key : `${PUBLIC_STORAGE_PREFIX}${key}`;

const getStorageDownloadKeyCandidates = (key) => {
  const publicKey = getPublicStorageKey(key);
  return key === publicKey ? [key] : [key, publicKey];
};

const refreshSessionOnce = () => {
  if (!refreshPromise) {
    refreshPromise = fetchAuthSession({ forceRefresh: true }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const toLegacyToken = (token) => ({
  getJwtToken: () => token?.toString?.() ?? '',
});

const toLegacySession = (session) => ({
  getIdToken: () => toLegacyToken(session?.tokens?.idToken),
  getAccessToken: () => toLegacyToken(session?.tokens?.accessToken),
  getRefreshToken: () => ({
    getToken: () => '',
  }),
});

const createMissingAuthTokensError = () => {
  const error = new Error('No valid auth tokens');
  error.name = 'NoValidAuthTokens';
  return error;
};

export const getCloudflareAuthHeaders = async () => {
  try {
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString?.();

    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};
  } catch {
    return {};
  }
};

// Amplify remains responsible for token validity and expiry; this only guards
// against a partially-hydrated session with missing user pool tokens.
const assertSessionHasTokens = (session) => {
  if (!session?.tokens?.idToken || !session?.tokens?.accessToken) {
    throw createMissingAuthTokensError();
  }
};

const toLegacyAuthenticatedUser = async () => {
  const [user, attributes, session] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes(),
    fetchAuthSession(),
  ]);
  assertSessionHasTokens(session);

  return {
    username: user?.username,
    attributes: attributes ?? {},
    signInUserSession: {
      idToken: { jwtToken: session.tokens.idToken.toString() },
      accessToken: { jwtToken: session.tokens.accessToken.toString() },
      refreshToken: { token: '' },
      clockDrift: 0,
    },
  };
};


const isAlreadyAuthenticatedError = (error) => {
  const code = error?.code || error?.name;
  return code === 'UserAlreadyAuthenticatedException';
};

const normalizeAuthAttributes = (attributes = {}) => {
  if (Array.isArray(attributes)) {
    return attributes.reduce((acc, item) => {
      if (item?.Name) {
        acc[item.Name] = item.Value ?? '';
      }
      return acc;
    }, {});
  }

  return attributes;
};

const getGraphqlOperationMetadata = (query) => {
  if (typeof query !== 'string') {
    return {
      type: 'anonymous',
      name: 'anonymous',
    };
  }

  const match = query
    .replace(/\s+/g, ' ')
    .trim()
    .match(/^(query|mutation|subscription)\s+([A-Za-z0-9_]+)/i);

  return {
    type: match?.[1]?.toLowerCase?.() ?? 'anonymous',
    name: match?.[2] ?? 'anonymous',
  };
};

const getGraphqlErrorDetails = (error) => {
  if (!Array.isArray(error?.errors)) {
    return [];
  }

  return error.errors.map((entry) => ({
    errorType: entry?.errorType ?? entry?.extensions?.errorType ?? 'GraphQLError',
    message: entry?.message ?? 'Unknown GraphQL error',
    path: Array.isArray(entry?.path) ? entry.path.join('.') : entry?.path ?? null,
  }));
};

const isAuthorizationMessage = (message = '') => AUTH_FAILURE_MESSAGE_PATTERN.test(message);

const isInvalidAuthSessionError = (error) => {
  if (!error) {
    return false;
  }

  const code = [error.name, error.code].filter(Boolean).join(' ');
  const message = [error.message, typeof error === 'string' ? error : null]
    .filter(Boolean)
    .join(' ');

  if (AUTH_FAILURE_CODE_PATTERN.test(code) || AUTH_FAILURE_MESSAGE_PATTERN.test(message)) {
    return true;
  }

  return isInvalidAuthSessionError(error.cause);
};

const forceLogoutForInvalidSession = (reason, error) => {
  if (!forceLogoutPromise) {
    logger.warn('Invalid auth session detected, forcing logout', {
      reason,
      errorName: error?.name,
      errorMessage: error?.message,
    });

    forceLogoutPromise = signOut()
      .catch((signOutError) => {
        captureException(signOutError, {
          tags: {
            module: 'amplifyCompat',
            operation: 'forceLogoutForInvalidSession',
          },
        });
      })
      .finally(() => {
        if (typeof window !== 'undefined') {
          const loginPath = '/';
          if (window.location?.pathname === loginPath) {
            window.location.reload();
          } else {
            window.location.assign(loginPath);
          }
        }
      });
  }

  return forceLogoutPromise;
};

const captureGraphqlException = (error, context) => {
  const graphQLErrorCount = context?.extra?.graphQLErrorCount ?? 0;

  if (graphQLErrorCount === 0 && isTransientNetworkError(error)) {
    logger.warn('Skipping Sentry capture for transient GraphQL network failure', {
      operationName: context?.tags?.operation,
      operationType: context?.tags?.operationType,
    });
    return undefined;
  }

  return captureException(error, context);
};

const getErrorCauseChain = (error) => {
  const causes = [];
  const seen = new Set();
  let current = error;
  let depth = 0;

  while (current && depth < 10) {
    if (typeof current !== 'object' || seen.has(current)) {
      break;
    }

    seen.add(current);
    causes.push(current);
    current = current.cause;
    depth += 1;
  }

  return causes;
};

const getGraphqlResponseDetails = (error) => {
  const detailsSource = getErrorCauseChain(error).find(
    (entry) => entry?.cloudflareResponseDetails
  );

  return detailsSource?.cloudflareResponseDetails ?? null;
};

const normalizeGraphqlError = (error, operationName) => {
  const graphqlErrors = getGraphqlErrorDetails(error);
  const responseDetails = getGraphqlResponseDetails(error);
  const messages = graphqlErrors
    .map((entry) => entry.message)
    .filter(Boolean);
  
  // Extract underlying error message from cause chain if available
  let underlyingMessage = null;
  let cause = error?.cause;
  let depth = 0;
  while (cause && !underlyingMessage && depth < 10) {
    if (cause?.message && cause.message !== error?.message) {
      underlyingMessage = cause.message;
    }
    cause = cause?.cause;
    depth++;
  }
  
  const fallbackMessage = [error?.message, typeof error === 'string' ? error : null].find(Boolean)
    ?? `GraphQL request failed for ${operationName}`;
  const isAuthorizationFailure =
    messages.length > 0
      ? messages.every(isAuthorizationMessage)
      : isAuthorizationMessage(fallbackMessage);
  const normalizedMessage =
    messages.length > 0
      ? isAuthorizationFailure
        ? 'GraphQL authorization failure'
        : [...new Set(messages)].join('; ')
      : isAuthorizationFailure
        ? 'GraphQL authorization failure'
        : fallbackMessage;
  const normalizedError =
    error instanceof Error && error.message === normalizedMessage
      ? error
      : new Error(normalizedMessage);

  if (!(error instanceof Error)) {
    normalizedError.cause = error;
  } else if (error !== normalizedError && !normalizedError.cause) {
    normalizedError.cause = error;
  }

  normalizedError.name = isAuthorizationFailure
    ? 'GraphQLAuthorizationError'
    : 'GraphQLRequestError';

  return {
    normalizedError,
    extra: {
      graphQLErrorCount: graphqlErrors.length,
      graphQLErrorMessages: messages,
      graphQLErrorTypes: graphqlErrors.map((entry) => entry.errorType),
      graphQLErrorPaths: graphqlErrors.map((entry) => entry.path).filter(Boolean),
      responseKeys:
        error && typeof error === 'object' ? Object.keys(error).sort() : [],
      cloudflareResponseStatus: responseDetails?.status,
      cloudflareResponseStatusText: responseDetails?.statusText,
      cloudflareResponseContentType: responseDetails?.contentType,
      cloudflareResponseBodyPreview: responseDetails?.bodyPreview,
      underlyingErrorMessage: underlyingMessage,
      operationName,
    },
  };
};

const isRetryableHttpStatus = (status) =>
  status === 408 || status === 429 || (status >= 500 && status <= 599);

const createInvalidJsonResponseError = (
  response,
  bodyText,
  parseError,
  requestLabel
) => {
  const contentType = response.headers.get('content-type') || '';
  const trimmedBody = bodyText.trim();
  const emptyBody = trimmedBody.length === 0;
  const error = new Error(
    emptyBody
      ? `${requestLabel} returned an empty response with ${response.status}`
      : `${requestLabel} returned invalid JSON with ${response.status}`
  );

  error.cause = parseError;
  error.status = response.status;
  error.cloudflareResponseDetails = {
    status: response.status,
    statusText: response.statusText,
    contentType,
    bodyPreview: trimmedBody.slice(0, 500),
  };
  error.isTransientNetworkError = emptyBody || isRetryableHttpStatus(response.status);

  return error;
};

const readJsonResponse = async (response, requestLabel) => {
  const bodyText = await response.text();

  if (!bodyText.trim()) {
    throw createInvalidJsonResponseError(
      response,
      bodyText,
      new SyntaxError('Unexpected end of JSON input'),
      requestLabel
    );
  }

  try {
    return JSON.parse(bodyText);
  } catch (parseError) {
    throw createInvalidJsonResponseError(
      response,
      bodyText,
      parseError,
      requestLabel
    );
  }
};

const graphqlViaCloudflare = async (graphqlOperation, signal) => {
  const headers = {
    'Content-Type': 'application/json',
    'X-Commit2Act-Client': 'web',
    ...(await getCloudflareAuthHeaders()),
  };

  const response = await fetch(buildCloudflareUrl('/api/graphql'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: graphqlOperation.query,
      variables: graphqlOperation.variables,
    }),
    signal,
  });

  const payload = await readJsonResponse(response, 'Cloudflare GraphQL request');

  if (!response.ok || Array.isArray(payload?.errors)) {
    const error = new Error(
      payload?.errors?.map((entry) => entry.message).join('; ') ||
        `Cloudflare GraphQL request failed with ${response.status}`
    );
    error.errors = payload?.errors || [];
    error.response = payload;
    error.status = response.status;
    error.cloudflareResponseDetails = {
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get('content-type') || '',
      bodyPreview: '',
    };
    error.isTransientNetworkError = isRetryableHttpStatus(response.status);
    throw error;
  }

  return payload;
};

const putViaCloudflare = async (key, data, options = {}) => {
  const response = await fetch(
    buildCloudflareUrl(`/uploads/${encodeStorageKey(key)}`),
    {
      method: 'PUT',
      headers: {
        ...(options.contentType ? { 'Content-Type': options.contentType } : {}),
        ...(await getCloudflareAuthHeaders()),
      },
      body: data,
    }
  );

  const payload = await response.json();

  if (!response.ok || payload?.errors) {
    throw new Error(
      payload?.errors?.map((entry) => entry.message).join('; ') ||
        `Cloudflare upload failed with ${response.status}`
    );
  }

  return {
    key: payload.key ?? key,
    url: payload.url,
  };
};

const getViaCloudflare = async (key, options = {}) => {
  const url = buildCloudflareUrl(`/media/${encodeStorageKey(key)}`);

  if (options.download) {
    const headers = {
      ...(await getCloudflareAuthHeaders()),
    };
    let lastStatus = null;

    for (const candidateKey of getStorageDownloadKeyCandidates(key)) {
      const response = await fetch(buildCloudflareUrl(`/media/${encodeStorageKey(candidateKey)}`), {
        headers,
      });

      if (response.ok) {
        return {
          Body: await response.blob(),
        };
      }

      lastStatus = response.status;

      if (response.status !== 404) {
        break;
      }
    }

    throw new Error(`Cloudflare media download failed with ${lastStatus}`);
  }

  return url;
};

export const API = {
  graphql: async (operation = {}) => {
    const {
      signal,
      suppressTransientNetworkErrorCapture = false,
      ...graphqlOperation
    } = operation;
    const operationMetadata = getGraphqlOperationMetadata(graphqlOperation.query);
    const operationName = operationMetadata.name;
    const operationType = operationMetadata.type;

    return startSpan(
      {
        op: 'graphql.client',
        name: `GraphQL ${operationName}`,
      },
      async (span) => {
        span.setAttribute('graphql.operation_name', operationName);
        span.setAttribute('graphql.operation_type', operationType);

        try {
          const executeOp = () => graphqlViaCloudflare(graphqlOperation, signal);
          return operationType === 'query'
            ? await withRetry(executeOp, {
                label: `GraphQL ${operationName}`,
                signal,
              })
            : await executeOp();
        } catch (error) {
          if (error?.name === 'AbortError') {
            throw error;
          }

          const { normalizedError, extra } = normalizeGraphqlError(error, operationName);

          // For authorization errors, try refreshing the session and retrying once
          if (normalizedError.name === 'GraphQLAuthorizationError' && !graphqlOperation.authMode) {
            logger.warn('GraphQL authorization failure, retrying with session refresh', {
              operationName,
              operationType,
            });

            try {
              await refreshSessionOnce();
              return await graphqlViaCloudflare(graphqlOperation, signal);
            } catch (retryError) {
              const { normalizedError: retryNormalizedError, extra: retryExtra } =
                normalizeGraphqlError(retryError, operationName);

              captureGraphqlException(retryNormalizedError, {
                tags: {
                  module: 'amplifyCompat',
                  operation: operationName,
                  operationType,
                  authRetry: 'session-refresh-failed',
                },
                extra: retryExtra,
              });
              logger.error('GraphQL request failed after session refresh retry', {
                operationName,
                operationType,
                graphQLErrorMessages: retryExtra.graphQLErrorMessages,
              });

              if (isInvalidAuthSessionError(retryError)) {
                await forceLogoutForInvalidSession('graphql-session-refresh-failed', retryNormalizedError);
              }

              throw retryNormalizedError;
            }
          }

          const shouldSuppressCapture =
            suppressTransientNetworkErrorCapture && isTransientNetworkError(normalizedError);

          if (shouldSuppressCapture) {
            logger.warn('GraphQL transient network request failed after retries', {
              operationName,
              operationType,
              graphQLErrorMessages: extra.graphQLErrorMessages,
            });
          } else {
            captureGraphqlException(normalizedError, {
              tags: {
                module: 'amplifyCompat',
                operation: operationName,
                operationType,
              },
              extra,
            });
            logger.error('GraphQL request failed', {
              operationName,
              operationType,
              graphQLErrorMessages: extra.graphQLErrorMessages,
            });
          }
          throw normalizedError;
        }
      }
    );
  },
};

export const Storage = {
  put: async (key, data, options = {}) => {
    return putViaCloudflare(key, data, options);
  },
  get: async (key, options = {}) => {
    return getViaCloudflare(key, options);
  },
};

export const Auth = {
  Credentials: {
    Auth: false,
  },
  currentSession: async () => {
    const session = await fetchAuthSession();
    assertSessionHasTokens(session);
    return toLegacySession(session);
  },
  currentAuthenticatedUser: () => toLegacyAuthenticatedUser(),
  currentUserInfo: async () => {
    const user = await toLegacyAuthenticatedUser();
    return {
      username: user.username,
      attributes: user.attributes,
    };
  },
  signUp: ({ username, password, attributes, ...rest }) =>
    signUp({
      username,
      password,
      options: { userAttributes: normalizeAuthAttributes(attributes) },
      ...rest,
    }),
  signIn: async (username, password) => {
    try {
      const result = await signIn({ username, password });
      if (result?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { challengeName: 'NEW_PASSWORD_REQUIRED' };
      }
      return result;
    } catch (error) {
      if (!isAlreadyAuthenticatedError(error)) {
        throw error;
      }

      return toLegacyAuthenticatedUser();
    }
  },
  signOut: () => signOut(),
  confirmSignUp: async (username, confirmationCode) => {
    const result = await confirmSignUp({ username, confirmationCode });
    if (result?.nextStep?.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
      await autoSignIn();
    }
    return result;
  },
  resendSignUp: (username) => resendSignUpCode({ username }),
  forgotPassword: (username) => resetPassword({ username }),
  forgotPasswordSubmit: (username, confirmationCode, newPassword) =>
    confirmResetPassword({ username, confirmationCode, newPassword }),
  completeNewPassword: (_user, newPassword) =>
    confirmSignIn({ challengeResponse: newPassword }),
  updateUserAttributes: (_user, userAttributes) =>
    updateUserAttributes({
      userAttributes: normalizeAuthAttributes(userAttributes),
    }),
  changePassword: (_user, oldPassword, newPassword) =>
    updatePassword({ oldPassword, newPassword }),
  deleteUser: () => deleteUser(),
  federatedSignIn: async (providerOrCustomIssuer) => {
    if (providerOrCustomIssuer?.provider) {
      return signInWithRedirect({ provider: providerOrCustomIssuer.provider });
    }

    if (typeof providerOrCustomIssuer === 'string') {
      return signInWithRedirect({ provider: { custom: providerOrCustomIssuer } });
    }

    return signInWithRedirect();
  },
};

export const Amplify = {
  configure: (...args) => {
    Auth.Credentials.Auth = true;
    return AmplifyCore.configure(...args);
  },
  getConfig: (...args) => AmplifyCore.getConfig(...args),
};

export { Hub };
